import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * THE BACK-NAVIGATION CONTRACT (Administration Part 1 §12).
 *
 * Back must return the user to WHERE THEY CAME FROM, not to a fixed destination:
 *
 *   Employee List → Ravi → Task → Property → Back → Task → Back → Ravi → Back → Employee List
 *
 * A `<Link to="/administration/departments">` cannot do that — it always lands in the same place, so
 * the fourth Back in that trail would jump the user to the top instead of one step out.
 *
 * ── IT USES THE ROUTER'S OWN HISTORY, NOT A CUSTOM STACK ────────────────────
 * §12 explicitly rules out a fragile bespoke global history, and React Router already keeps the real
 * one: every in-app entry it pushes carries an `idx` in `window.history.state`, and the first entry
 * of a session has `location.key === "default"`. So:
 *
 *   there IS a previous in-app entry  →  `navigate(-1)`, which restores the actual originating
 *                                        context, including that page's scroll and its query string;
 *   there is NOT — a bookmark, a      →  `navigate(fallbackTo)`, the page's declared parent, so Back
 *   pasted deep link, a fresh tab        is never a dead button and never exits the application.
 *
 * The fallback is the ONLY place a fixed destination appears, and it is used only when there is
 * genuinely nothing to go back to.
 *
 * It lives in `utils/` beside the other shared non-component logic so that `BackLink.jsx` exports a
 * component and nothing else — a page needing its own trigger (a link, a keyboard shortcut, an
 * action inside a menu) calls this hook directly.
 */
export function useBackNavigation(fallbackTo) {
  const navigate = useNavigate();
  const location = useLocation();

  // `?? 0` covers the browsers and embeddings where `history.state` has been replaced wholesale; the
  // safe reading of "unknown" is "no in-app history", which sends Back to the declared parent.
  const hasInAppHistory = (window.history.state?.idx ?? 0) > 0 && location.key !== "default";

  return useCallback(() => {
    if (hasInAppHistory) navigate(-1);
    else navigate(fallbackTo, { replace: true });
  }, [hasInAppHistory, navigate, fallbackTo]);
}

/**
 * `?from=` IS ATTACKER-SUPPLIED, AND IT BECOMES A NAVIGATION TARGET.
 *
 * Registration & Sales Part 8 §22 recorded this as a known follow-up: several screens read a `from`
 * query parameter and hand it straight to `BackLink fallbackTo=` or to a `<Link to=>`, so a crafted
 * link plus a deliberate click could produce an EXTERNAL Back target. It was classified PRE-EXISTING,
 * NON-BLOCKING and NOT FIXED, with the recommended remedy written down: "Accept only internal paths
 * beginning with a single `/`; reject anything else." This is that remedy.
 *
 * ── WHY A LEADING SINGLE SLASH IS THE WHOLE RULE ────────────────────────────
 * `https://evil.example.com/steal` has no leading slash, so the router already resolves it as a
 * RELATIVE path and it never leaves the origin. The value that does leave is the PROTOCOL-RELATIVE
 * form — `//evil.example.com/steal` — which a browser reads as "same scheme, that host". Rendered by
 * `<Link>` it becomes a genuine off-site anchor. `/\evil.example.com` is the same attack: browsers
 * normalise a backslash to a forward slash in the authority position, so `/\` must be refused
 * alongside `//`.
 *
 * ── IT VALIDATES, IT DOES NOT REWRITE ───────────────────────────────────────
 * A rejected value falls back to the page's own declared parent rather than being "cleaned" into
 * something adjacent. Sanitising by rewriting invites a second bug the day the rewrite is incomplete;
 * refusing is total.
 *
 * Query string and hash are PRESERVED, because that is the point of `from`: Part 6 §30's boards carry
 * their filters in the query, and a Back that dropped them would return to a reset list rather than
 * to the context the reader left.
 *
 * @param {unknown} value     the untrusted `from` value, usually `searchParams.get("from")`
 * @param {string}  fallback  this page's own declared parent, used when `value` is absent or refused
 * @returns {string} an in-application path, always
 */
export function safeInternalPath(value, fallback) {
  if (typeof value !== "string" || value === "") return fallback;
  // Control characters (including a newline or a tab smuggled in percent-encoded form after the
  // browser has decoded the query) have no place in a path and are refused outright.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(value)) return fallback;
  // Exactly one leading slash, and never a second slash or a backslash behind it.
  if (value[0] !== "/") return fallback;
  if (value[1] === "/" || value[1] === "\\") return fallback;
  return value;
}
